// Hang slots (docs/contracts.md section 8): fixed places on the walls where a picture can hang, read from
// public/art/slots/<scene>.json. Picking is plain math against the walls' planes (no scene raycast): a ray meets
// each wall plane once, and the point is tested against the slots' rectangles in that wall's own axes.
//
// Frame shapes (2026-10-25): a picture hangs in the frame shape of its slot kind whose picture aspect is nearest the
// image's own (w / h, compared as ratios). A portrait kind (slots.json `aspect` < 1, the panel frames) has three:
// portrait (`aspect`), landscape (its inverse) and square (1). A landscape kind (the large frames) has only its own
// landscape frame (user 2026-10-25): every image is cover-cropped into it. A kind may list its own in `shapes`.
// Landscape and square keep the kind's long side (`size`) unless the frame would not fit the slot's box: a panel
// slot's moulding opening (`area`) less PANEL_MARGIN on each side, or for a slot without an area the outer size of its
// default frame. The portrait panel frame takes the square frame's width, its outer frame is 3:4 (0.74 x 0.987 m), and
// it may be taller than the moulding opening (user 2026-10-25: it overlaps the mouldings). Every frame is centred on the slot centre. With no mat the outer size
// stays the same and the picture takes the whole opening.

import { Vector3, type Ray } from 'three';
import { frameLayout, placement, PROFILE, type Finish, type FrameLayout, type FrameSpec, type Placement } from './frame';

export type Shape = 'portrait' | 'landscape' | 'square';
export const SHAPES: readonly Shape[] = ['portrait', 'landscape', 'square'];
/** Wall left free between a panel frame and the moulding opening's edge (each side, m). */
export const PANEL_MARGIN = 0.05;

export interface FrameKind {
  size: number;
  aspect: number;
  finish: Finish;
  mat: number;
  hang: number;
  max_px: number;
  /** picture aspect (w / h) of each frame shape the kind has */
  shapes: Partial<Record<Shape, number>>;
}

/** One frame of a slot: its shape, spec and sizes. */
export interface FrameFit {
  shape: Shape;
  spec: FrameSpec;
  layout: FrameLayout;
}

export interface Slot {
  id: string;
  name: string;
  wall: string;
  kind: string;
  frame: FrameKind;
  /** the default frame (the kind's aspect): the empty slot's outline and the panel's drop zone */
  shape: Shape;
  spec: FrameSpec;
  layout: FrameLayout;
  /** the frame of each shape, with the kind's mat */
  fits: Partial<Record<Shape, FrameFit>>;
  /** wall rectangle sampled for the frame's light (covers every shape) */
  sampleW: number;
  sampleH: number;
  /** frame centre on the wall surface + the wall's axes */
  pl: Placement;
  /** half extents of the rectangle that selects the slot (wall axes, m) */
  hw: number;
  hh: number;
  /** slots this one covers / slots covering this one */
  over: string[];
  coveredBy: string[];
}

interface Wall { id: string; name: string; normal: Vector3; point: Vector3; slots: Slot[] }

interface SlotsJson {
  frames: Record<string, Partial<FrameKind>>;
  walls: { id: string; name?: string; normal: [number, number, number];
           slots: { id: string; name?: string; frame: string; center: [number, number, number]; area?: [number, number]; over?: string[] }[] }[];
}

/** The shape whose picture aspect is nearest the image's (w / h; ratios compared, so 4:3 and 3:4 are as far from 1:1). */
export function shapeFor(kind: FrameKind, w: number, h: number): Shape {
  const r = Math.log(Math.max(1e-6, w) / Math.max(1e-6, h));
  let best: Shape = 'square', bd = Infinity;
  for (const s of SHAPES) {
    const a = kind.shapes[s];
    if (!a) continue;
    const d = Math.abs(r - Math.log(a));
    if (d < bd - 1e-9) { bd = d; best = s; }
  }
  return best;
}

/**
 * The largest long side (at most `size`) for a frame of picture aspect `a` and border `b` (both sides, m) whose outer
 * rectangle fits in bw x bh.
 */
function fitSize(size: number, a: number, b: number, bw: number, bh: number): number {
  // a >= 1: outer w = size, outer h = (size - b) / a + b; a < 1: outer h = size, outer w = (size - b) * a + b
  const lim = a >= 1 ? Math.min(bw, (bh - b) * a + b) : Math.min(bh, (bw - b) / a + b);
  return Math.max(b + 0.04, Math.min(size, lim));
}

function makeFit(kind: FrameKind, shape: Shape, bw: number, bh: number, size = kind.size, aspect = kind.shapes[shape]!): FrameFit {
  size = fitSize(size, aspect, 2 * (PROFILE.face + kind.mat), bw, bh);
  const spec: FrameSpec = { aspect, size, finish: kind.finish, mat: kind.mat, hang: kind.hang };
  return { shape, spec, layout: frameLayout(spec) };
}

/** A picture's frame size and look (only w, h and mat are read; no entry = the slot's default frame). */
export interface FrameOf { w: number; h: number; mat?: string }

/**
 * The frame a picture hangs in on `slot`: the shape nearest its aspect; with mat "none" the same outer size, no mat
 * and the picture filling the opening.
 */
export function frameFor(slot: Slot, e?: FrameOf | { mat?: string }): FrameFit {
  const fit = e && 'w' in e ? slot.fits[shapeFor(slot.frame, e.w, e.h)]! : slot.fits[slot.shape]!;
  if (e?.mat !== 'none' || !fit.spec.mat) return fit;
  const l = fit.layout;
  const border = 2 * PROFILE.face;
  return { shape: fit.shape, spec: { ...fit.spec, mat: 0 }, layout: { outerW: l.outerW, outerH: l.outerH, imageW: l.outerW - border, imageH: l.outerH - border } };
}

/**
 * The part of a w x h image a texture keeps for `slot` (centred): the cover crop at the picture aspect of its shape,
 * widened to cover the same frame without a mat too (a mat change then needs no reload).
 */
export function cropRect(slot: Slot, w: number, h: number): { sw: number; sh: number } {
  const shape = shapeFor(slot.frame, w, h);
  const l1 = slot.fits[shape]!.layout;
  const a1 = l1.imageW / l1.imageH;
  const l0 = frameFor(slot, { w, h, mat: 'none' }).layout;
  const a0 = l0.imageW / l0.imageH;
  return { sw: Math.min(w, h * Math.max(a0, a1)), sh: Math.min(h, w / Math.min(a0, a1)) };
}

const _p = new Vector3();
const _d = new Vector3();

export class SlotSet {
  readonly slots = new Map<string, Slot>();
  private walls: Wall[] = [];
  /** Wall-plane tests done (for tests). */
  readonly stats = { picks: 0 };
  /**
   * The largest max_px of any frame kind: uploads keep their aspect and are scaled to this (one library image can
   * hang in any frame on its wall; Artworks crops it to the frame it hangs in).
   */
  maxPx = 16;

  constructor(json: SlotsJson) {
    for (const w of json.walls) {
      const normal = new Vector3(...w.normal).normalize();
      const wall: Wall = { id: w.id, name: w.name ?? w.id, normal, point: new Vector3(), slots: [] };
      for (const s of w.slots) {
        const k = json.frames[s.frame];
        if (!k) throw new Error(`slots: ${s.id}: no frame kind ${s.frame}`);
        const aspect = k.aspect ?? 0.75;
        const shapes = k.shapes ?? (aspect >= 1 ? { landscape: aspect } : { portrait: aspect, landscape: 1 / aspect, square: 1 });
        const frame: FrameKind = { size: k.size ?? 0.74, aspect, finish: k.finish ?? 'warm_white', mat: k.mat ?? 0.05,
                                   hang: k.hang ?? 0.0005, max_px: k.max_px ?? 1600, shapes };
        const shape = shapeFor(frame, aspect, 1);
        // the box every shape fits in: the moulding opening less the margin, else the default frame's outer size
        const base = frameLayout({ aspect, size: frame.size, finish: frame.finish, mat: frame.mat });
        const [bw, bh] = s.area ? [s.area[0] - 2 * PANEL_MARGIN, s.area[1] - 2 * PANEL_MARGIN] : [base.outerW, base.outerH];
        const kinds = SHAPES.filter((sh) => !!shapes[sh]);
        const fits: Partial<Record<Shape, FrameFit>> = {};
        for (const sh of kinds) if (sh !== 'portrait' || !shapes.square) fits[sh] = makeFit(frame, sh, bw, bh);
        // the portrait frame of a kind with a square one: the square frame's width, the outer frame at the portrait
        // aspect (3:4: 0.74 x 0.987 m; the picture inside is a little narrower than 3:4)
        if (shapes.portrait && fits.square) {
          const ow = fits.square.layout.outerW, b = 2 * (PROFILE.face + frame.mat);
          const oh = ow / shapes.portrait;
          fits.portrait = makeFit(frame, 'portrait', ow, oh + 1e-9, oh, (ow - b) / (oh - b));
        }
        const { spec, layout } = fits[shape]!;
        const pl = placement(new Vector3(...s.center), normal);
        const all = kinds.map((sh) => fits[sh]!.layout);
        const sampleW = Math.max(...all.map((l) => l.outerW));
        const sampleH = Math.max(...all.map((l) => l.outerH));
        // the rectangle that selects the slot: the moulding opening, grown to every frame (a tall portrait frame)
        const [aw, ah] = s.area ? [Math.max(s.area[0], sampleW), Math.max(s.area[1], sampleH)] : [layout.outerW, layout.outerH];
        const slot: Slot = { id: s.id, name: s.name ?? s.id, wall: w.id, kind: s.frame, frame, shape, spec, layout, fits, sampleW, sampleH, pl,
                             hw: aw / 2, hh: ah / 2, over: s.over ?? [], coveredBy: [] };
        this.slots.set(slot.id, slot);
        wall.slots.push(slot);
        this.maxPx = Math.max(this.maxPx, frame.max_px);
      }
      if (wall.slots.length) wall.point.copy(wall.slots[0]!.pl.origin);
      this.walls.push(wall);
    }
    for (const s of this.slots.values()) for (const o of s.over) this.slots.get(o)?.coveredBy.push(s.id);
  }

  /** Remove every slot of a frame kind (e.g. the gallery's `view` slot in a build without the window view plane). */
  removeKind(kind: string): void {
    for (const [id, s] of [...this.slots]) if (s.kind === kind) this.slots.delete(id);
    for (const w of this.walls) w.slots = w.slots.filter((s) => s.kind !== kind);
    this.walls = this.walls.filter((w) => w.slots.length);
  }

  static async load(url: string): Promise<SlotSet> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
    return new SlotSet((await res.json()) as SlotsJson);
  }

  get(id: string): Slot | undefined {
    return this.slots.get(id);
  }

  /** Slots that may not hold a picture at the same time as `id` (it covers them, or they cover it). */
  conflicts(id: string): string[] {
    const s = this.slots.get(id);
    return s ? [...s.over, ...s.coveredBy] : [];
  }

  /** The large slot on the same wall as `id` (or `id` itself if it is one), if the wall has one. */
  largeOf(id: string): Slot | undefined {
    const s = this.slots.get(id);
    if (!s) return undefined;
    if (s.over.length) return s;
    for (const o of this.slots.values()) if (o.wall === s.wall && o.over.length) return o;
    return undefined;
  }

  /** A panel slot under the large slot `id` (the one nearest its centre), for switching back from large. */
  panelUnder(id: string): Slot | undefined {
    const s = this.slots.get(id);
    if (!s) return undefined;
    let best: Slot | undefined, bd = Infinity;
    for (const o of s.over) {
      const t = this.slots.get(o);
      const d = t ? t.pl.origin.distanceTo(s.pl.origin) : Infinity;
      if (d < bd) { bd = d; best = t; }
    }
    return best;
  }

  /**
   * The slot under a ray, and its distance. `filled(id)`: whether the slot holds a picture. A slot covered by a
   * filled slot is not picked; an empty slot is not picked while a slot it conflicts with is filled (it could
   * not take a picture). A filled slot wins over an empty one; among empty ones the smaller rectangle wins
   * (a panel opening inside the large slot's rectangle picks the panel).
   */
  pick(ray: Ray, filled: (id: string) => boolean): { id: string; distance: number } | null {
    this.stats.picks++;
    let best: Slot | null = null;
    let bestT = Infinity;
    let bestRank = Infinity;
    for (const w of this.walls) {
      const denom = ray.direction.dot(w.normal);
      if (denom >= -1e-6) continue; // looking away from (or along) the wall
      const t = _d.subVectors(w.point, ray.origin).dot(w.normal) / denom;
      if (t <= 0 || t > bestT + 1e-6) continue;
      const p = _p.copy(ray.origin).addScaledVector(ray.direction, t);
      for (const s of w.slots) {
        _d.subVectors(p, s.pl.origin);
        if (Math.abs(_d.dot(s.pl.right)) > s.hw || Math.abs(_d.dot(s.pl.up)) > s.hh) continue;
        const full = filled(s.id);
        if (!full) {
          if (s.coveredBy.some(filled) || s.over.some(filled)) continue;
        } else if (s.coveredBy.some(filled)) continue;
        const rank = (full ? 0 : 1e3) + s.hw * s.hh;
        if (t < bestT - 1e-6 || rank < bestRank) { best = s; bestT = t; bestRank = rank; }
      }
    }
    return best ? { id: best.id, distance: bestT } : null;
  }
}
